import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { Topbar } from "../components/shell/Topbar";
import { useToast } from "../components/shell/Toast";
import { useLayoutContext } from "../layouts/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { cn } from "@/packages/utils/cn";

type Tab = "general" | "api-keys";

const PALETTE = ["#C8362C", "#3E7C5C", "#C7903A", "#8C7AB0", "#1B1F2A"];

interface Project {
  id: string;
  referenceId: string;
  name: string;
  slug: string;
  description: string | null;
  color: string;
}

interface ApiKey {
  id: string;
  projectId: string;
  name: string;
  keyPrefix: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export function ProjectSettingsPage() {
  const { teamId, projectId } = useParams();
  const { teams, projects, refreshProjects } = useLayoutContext();
  const team = teams.find((t) => t.id === teamId);
  const project = (projects[teamId ?? ""] ?? []).find(
    (p) => p.referenceId === projectId,
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get("tab") as Tab) ?? "general";
  const [tab, setTab] = useState<Tab>(initialTab);

  const setActiveTab = (t: Tab) => {
    setTab(t);
    searchParams.set("tab", t);
    setSearchParams(searchParams, { replace: true });
  };

  if (!team || !project) return null;

  const tabs: Array<{ id: Tab; label: string }> = [
    { id: "general", label: "General" },
    { id: "api-keys", label: "API keys" },
  ];

  return (
    <>
      <Topbar
        title="Project settings"
        crumbs={[
          { label: team.name, to: `/teams/${team.id}` },
          {
            label: project.name,
            to: `/teams/${team.id}/projects/${project.referenceId}`,
          },
          { label: "Settings" },
        ]}
      />
      <div className="border-b border-rule px-4 sm:px-8 flex gap-1 bg-paper">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={cn(
              "px-3.5 py-3 text-sm border-b-2 transition-colors",
              tab === t.id
                ? "border-foreground text-foreground font-semibold"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-auto px-4 sm:px-8 py-6">
        {tab === "general" && (
          <GeneralTab
            teamId={team.id}
            projectRef={project.referenceId}
            onSaved={refreshProjects}
          />
        )}
        {tab === "api-keys" && (
          <ApiKeysTab projectRef={project.referenceId} />
        )}
      </div>
    </>
  );
}

// ─── General ───────────────────────────────────────────────────────────

function GeneralTab({
  teamId,
  projectRef,
  onSaved,
}: {
  teamId: string;
  projectRef: string;
  onSaved: () => void;
}) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [project, setProject] = useState<Project | null>(null);
  const [name, setName] = useState("");
  const [color, setColor] = useState(PALETTE[0]!);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  useEffect(() => {
    fetch(`/api/teams/${teamId}/projects/${projectRef}`)
      .then((r) => r.json())
      .then((p: Project) => {
        setProject(p);
        setName(p.name);
        setColor(p.color);
      });
  }, [teamId, projectRef]);

  const dirty =
    project !== null && (name !== project.name || color !== project.color);

  const save = async () => {
    if (!project || saving) return;
    setError(null);
    setSaving(true);
    try {
      const res = await fetch(`/api/teams/${teamId}/projects/${projectRef}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, color }),
      });
      if (!res.ok) {
        setError((await res.json()).error ?? "Save failed");
        return;
      }
      const next = (await res.json()) as Project;
      setProject(next);
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  const copyId = async () => {
    if (!project) return;
    await navigator.clipboard.writeText(project.referenceId);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (!project) {
    return (
      <div className="text-sm text-muted-foreground italic">Loading…</div>
    );
  }

  return (
    <div className="max-w-xl space-y-6">
      <section>
        <Label className="text-eyebrow">Project ID</Label>
        <div className="mt-1.5 flex items-center gap-2">
          <code className="flex-1 px-3 py-2 rounded-md border border-rule bg-panel/40 font-mono text-xs select-all">
            {project.referenceId}
          </code>
          <Button size="sm" variant="outline" onClick={copyId}>
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
        <p className="mt-1.5 text-xs text-muted-foreground">
          Use this ID in API calls and SDK config. It cannot be changed.
        </p>
      </section>

      <section className="space-y-1.5">
        <Label htmlFor="project-name">Name</Label>
        <Input
          id="project-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={saving}
        />
      </section>

      <section className="space-y-1.5">
        <Label>Color</Label>
        <div className="flex gap-2">
          {PALETTE.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setColor(c)}
              disabled={saving}
              className={cn(
                "size-8 rounded-md border-2 transition-transform",
                color === c
                  ? "border-foreground scale-110"
                  : "border-transparent hover:scale-105",
              )}
              style={{ background: c }}
              aria-label={c}
            />
          ))}
        </div>
      </section>

      {error && <div className="text-sm text-signal-red">{error}</div>}

      <div className="flex justify-end">
        <Button onClick={save} disabled={!dirty || saving || !name.trim()}>
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <section className="border-t border-signal-red/30 pt-6">
        <h2 className="font-serif text-lg font-semibold tracking-tight text-signal-red">
          Danger zone
        </h2>
        <p className="text-sm text-muted-foreground mt-1 mb-4">
          Deleting this project permanently removes its API mocks, webhooks,
          feature flags, releases, and API keys. This cannot be undone.
        </p>
        <Button variant="destructive" onClick={() => setShowDelete(true)}>
          Delete project
        </Button>
      </section>

      {showDelete && (
        <DeleteProjectDialog
          teamId={teamId}
          projectRef={projectRef}
          projectName={project.name}
          onClose={() => setShowDelete(false)}
          onDeleted={() => {
            setShowDelete(false);
            toast({ tone: "success", title: `Deleted ${project.name}` });
            onSaved();
            navigate(`/teams/${teamId}`);
          }}
        />
      )}
    </div>
  );
}

function DeleteProjectDialog({
  teamId,
  projectRef,
  projectName,
  onClose,
  onDeleted,
}: {
  teamId: string;
  projectRef: string;
  projectName: string;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canDelete = confirmText === "delete me" && !deleting;

  const handleDelete = async () => {
    if (!canDelete) return;
    setError(null);
    setDeleting(true);
    try {
      const res = await fetch(
        `/api/teams/${teamId}/projects/${projectRef}`,
        { method: "DELETE" },
      );
      if (!res.ok) {
        setError(
          (await res.json().catch(() => ({}))).error ?? "Delete failed",
        );
        return;
      }
      onDeleted();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Network error");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !deleting && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete {projectName}?</DialogTitle>
          <DialogDescription>
            This permanently removes the project and all of its mocks,
            webhooks, feature flags, releases, and API keys. This action
            cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="delete-confirm">
            Type{" "}
            <code className="font-mono text-signal-red">delete me</code> to
            confirm
          </Label>
          <Input
            id="delete-confirm"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            autoComplete="off"
            disabled={deleting}
          />
          {error && <div className="text-sm text-signal-red">{error}</div>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={deleting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={!canDelete}
          >
            {deleting ? "Deleting…" : "Delete project"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── API keys ──────────────────────────────────────────────────────────

function ApiKeysTab({ projectRef }: { projectRef: string }) {
  const [keys, setKeys] = useState<ApiKey[] | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [revealedKey, setRevealedKey] = useState<{
    rawKey: string;
    name: string;
  } | null>(null);

  const refresh = async () => {
    const res = await fetch(`/api/projects/${projectRef}/api-keys`);
    setKeys(await res.json());
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectRef]);

  const revoke = async (id: string) => {
    if (!confirm("Revoke this API key? Existing requests using it will fail.")) {
      return;
    }
    await fetch(`/api/projects/${projectRef}/api-keys/${id}`, {
      method: "DELETE",
    });
    refresh();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">API keys</h2>
          <p className="text-sm text-muted-foreground">
            Keys grant project-admin-equivalent access to every endpoint of
            this project. Treat them like passwords.
          </p>
        </div>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          + New API key
        </Button>
      </div>

      {keys === null ? (
        <div className="text-sm text-muted-foreground italic">Loading…</div>
      ) : keys.length === 0 ? (
        <div className="border border-dashed border-rule rounded-lg px-6 py-10 text-center text-sm text-muted-foreground italic">
          No keys yet. Create one to start calling the API from outside the
          dashboard.
        </div>
      ) : (
        <div className="border border-rule rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-rule bg-card-paper">
                <th className="text-left px-4 py-2.5 text-eyebrow">Name</th>
                <th className="text-left px-4 py-2.5 text-eyebrow">Prefix</th>
                <th className="text-left px-4 py-2.5 text-eyebrow">Created</th>
                <th className="text-left px-4 py-2.5 text-eyebrow">
                  Last used
                </th>
                <th className="text-left px-4 py-2.5 text-eyebrow">Expires</th>
                <th className="text-left px-4 py-2.5 text-eyebrow">Status</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <ApiKeyRow key={k.id} k={k} onRevoke={() => revoke(k.id)} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateKeyDialog
          projectRef={projectRef}
          onClose={() => setShowCreate(false)}
          onCreated={(rawKey, name) => {
            setShowCreate(false);
            setRevealedKey({ rawKey, name });
            refresh();
          }}
        />
      )}

      {revealedKey && (
        <RevealKeyDialog
          rawKey={revealedKey.rawKey}
          name={revealedKey.name}
          onClose={() => setRevealedKey(null)}
        />
      )}
    </div>
  );
}

function ApiKeyRow({
  k,
  onRevoke,
}: {
  k: ApiKey;
  onRevoke: () => void;
}) {
  const isRevoked = Boolean(k.revokedAt);
  const isExpired =
    !isRevoked && k.expiresAt !== null && new Date(k.expiresAt) <= new Date();
  const status = isRevoked ? "revoked" : isExpired ? "expired" : "active";
  return (
    <tr className="border-b border-rule-soft last:border-b-0">
      <td className="px-4 py-2.5 font-medium">{k.name}</td>
      <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">
        {k.keyPrefix}…
      </td>
      <td className="px-4 py-2.5 font-mono text-xs">
        {format(new Date(k.createdAt), "yyyy-MM-dd")}
      </td>
      <td className="px-4 py-2.5 font-mono text-xs">
        {k.lastUsedAt ? format(new Date(k.lastUsedAt), "yyyy-MM-dd") : "—"}
      </td>
      <td className="px-4 py-2.5 font-mono text-xs">
        {k.expiresAt ? format(new Date(k.expiresAt), "yyyy-MM-dd") : "Never"}
      </td>
      <td className="px-4 py-2.5">
        <span
          className={cn(
            "px-2 py-0.5 rounded-full font-mono text-[10px] font-bold uppercase",
            status === "active" &&
              "bg-signal-ok/15 text-signal-ok border border-signal-ok/30",
            status === "expired" &&
              "bg-signal-warn/15 text-signal-warn border border-signal-warn/30",
            status === "revoked" &&
              "bg-signal-red/15 text-signal-red border border-signal-red/30",
          )}
        >
          {status}
        </span>
      </td>
      <td className="px-4 py-2.5 text-right">
        {!isRevoked && (
          <Button variant="ghost" size="sm" onClick={onRevoke}>
            Revoke
          </Button>
        )}
      </td>
    </tr>
  );
}

function CreateKeyDialog({
  projectRef,
  onClose,
  onCreated,
}: {
  projectRef: string;
  onClose: () => void;
  onCreated: (rawKey: string, name: string) => void;
}) {
  const [name, setName] = useState("");
  const [expiresAt, setExpiresAt] = useState<Date | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (submitting || !name.trim()) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(
        `/api/projects/${projectRef}/api-keys`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: name.trim(),
            expiresAt: expiresAt ? expiresAt.toISOString() : null,
          }),
        },
      );
      if (!res.ok) {
        setError((await res.json()).error ?? "Create failed");
        return;
      }
      const created = (await res.json()) as { rawKey: string };
      onCreated(created.rawKey, name.trim());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New API key</DialogTitle>
          <DialogDescription>
            Grants full project access. The raw key is shown once on
            creation — copy it immediately.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="api-key-name">Name</Label>
            <Input
              id="api-key-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Production server"
              disabled={submitting}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Expires</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-start font-mono text-xs"
                  disabled={submitting}
                >
                  {expiresAt
                    ? format(expiresAt, "yyyy-MM-dd")
                    : "Never expires"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={expiresAt}
                  onSelect={setExpiresAt}
                  disabled={(d) => d <= new Date()}
                  autoFocus
                />
                <div className="flex justify-between border-t border-rule px-3 py-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setExpiresAt(undefined)}
                  >
                    Clear
                  </Button>
                  <span className="text-xs text-muted-foreground self-center">
                    Leave empty for no expiration
                  </span>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          {error && <div className="text-sm text-signal-red">{error}</div>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!name.trim() || submitting}>
            {submitting ? "Creating…" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevealKeyDialog({
  rawKey,
  name,
  onClose,
}: {
  rawKey: string;
  name: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(rawKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>API key created</DialogTitle>
          <DialogDescription>
            <b>{name}</b> is ready. This is the only time the full key is
            shown — save it somewhere safe right now.
          </DialogDescription>
        </DialogHeader>
        <div className="border border-signal-warn/40 bg-signal-warn/5 rounded-md px-3 py-2 mb-3 text-xs">
          Once you close this dialog the key cannot be retrieved. You'd have
          to revoke and recreate it.
        </div>
        <div className="flex items-center gap-2">
          <code className="flex-1 px-3 py-2 rounded-md border border-rule bg-panel/40 font-mono text-xs break-all select-all">
            {rawKey}
          </code>
          <Button size="sm" variant="outline" onClick={copy}>
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
        <DialogFooter>
          <Button onClick={onClose}>I've saved it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
